import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Banner, EmptyState, SectionHead, Skeleton, Toast } from '../primitives';
import { ViewTabs, SearchInput, GroupByMenu, type GroupByOption } from '../list-controls';
import { DocumentsGallery, GALLERY_DEFAULT_CHIPS } from '../documents/DocumentsGallery';
import { DocumentsTable, TABLE_DEFAULT_VISIBLE } from '../documents/DocumentsTable';
import {
  useDocumentsListVM,
  type DocumentsListInitialOverrides
} from '../../lib/vm/useDocumentsListVM';
import { getDocumentsView, setDocumentsView } from '../../lib/viewPreferences';
import type { DocumentRow, DocumentsTab } from '../../lib/models/documents';
import type { BulkAction, CellValue } from '../Table';

const TAB_OPTIONS = [
  { key: 'gallery', label: 'Gallery' },
  { key: 'table', label: 'Table' }
] as const;

/**
 * Group-by axes offered on the project Documents surface. The **Type** axis is
 * the role grouping — it groups linked documents by their *actual* `type_id`,
 * every org type equal, none privileged, with an "Untyped" group for null-type
 * docs (the gallery's `groupRows` owns that fallback). Mirrors the
 * `/documents` page's `DOC_GROUP_BY_OPTIONS`; no special research/context/PRD/
 * plan buckets and no "Other" catch-all.
 */
const PROJECT_DOC_GROUP_BY_OPTIONS: ReadonlyArray<GroupByOption> = [
  { key: 'none', label: 'None' },
  { key: 'type', label: 'Type' },
  { key: 'lifecycle', label: 'Lifecycle' },
  { key: 'owner', label: 'Owner' }
];

export interface ProjectDocumentsSectionProps {
  projectId: string;
  onOpenDocument: (id: string) => void;
  /**
   * Bumps when a document is created/attached (or the editor cover closes) so
   * the gallery/table refetches in place — otherwise a newly-linked doc only
   * shows after a manual page refresh. Mirrors the Work Items section signal.
   */
  reloadSignal?: number;
  /** "New ▾" header control (create-or-attach menu). */
  newMenu?: ReactNode;
}

export function ProjectDocumentsSection({
  projectId,
  onOpenDocument,
  reloadSignal,
  newMenu
}: ProjectDocumentsSectionProps) {
  // Seed the view from the global preference (stored → gallery). No URL read:
  // the project canvas keeps view state out of its URL (urlMode: 'local').
  const initialOverrides = useMemo<DocumentsListInitialOverrides>(
    () => ({ tab: getDocumentsView() ?? 'gallery' }),
    []
  );
  const vm = useDocumentsListVM({
    overrides: initialOverrides,
    projectId,
    urlMode: 'local'
  });

  // Refetch in place when the parent bumps `reloadSignal` (doc created/attached
  // or editor cover closed). Skip the initial render — the VM already fetches on
  // mount, so reacting to the first signal value would double-fetch. Keyed only
  // on `reloadSignal`; the reload fn is read via ref so unrelated VM state
  // changes (filter/sort/page) don't re-trigger a fetch.
  const reloadRef = useRef(vm.actions.reload);
  reloadRef.current = vm.actions.reload;
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    reloadRef.current();
  }, [reloadSignal]);

  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set<string>()
  );
  const [toast, setToast] = useState<string | null>(null);

  const rows = vm.derived.rows;

  const typeLookup = useMemo(
    () => new Map(vm.derived.typeOptions.map((t) => [t.id, t.name] as const)),
    [vm.derived.typeOptions]
  );
  const ownerLookup = useMemo(
    () =>
      new Map(
        vm.derived.memberOptions.map((m) => [m.userId, m.displayName] as const)
      ),
    [vm.derived.memberOptions]
  );
  const visibleProperties = useMemo<ReadonlyArray<string>>(
    () =>
      vm.state.cols.length > 0
        ? vm.state.cols
        : vm.state.tab === 'gallery'
          ? [...GALLERY_DEFAULT_CHIPS]
          : [...TABLE_DEFAULT_VISIBLE],
    [vm.state.cols, vm.state.tab]
  );

  const onToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleCellEdit = useCallback(
    async (rowId: string, columnKey: string, newValue: CellValue) => {
      let result: { ok: boolean; error?: string } | null = null;
      let fieldLabel = columnKey;
      try {
        if (columnKey === 'lifecycle' && typeof newValue === 'string') {
          fieldLabel = 'Lifecycle';
          result = await vm.actions.setLifecycle(
            rowId,
            newValue as 'draft' | 'published' | 'archived'
          );
        } else if (columnKey === 'type') {
          fieldLabel = 'Type';
          if (typeof newValue === 'string' && newValue.length > 0) {
            result = await vm.actions.setDocumentType(rowId, newValue);
          }
        } else if (columnKey === 'owner') {
          fieldLabel = 'Owner';
          const next =
            typeof newValue === 'string' && newValue.length > 0 ? newValue : null;
          result = await vm.actions.setOwner(rowId, next);
        }
      } catch (err) {
        result = { ok: false, error: String(err) };
      }
      if (result && !result.ok) setToast(`Couldn't save ${fieldLabel} — try again`);
    },
    [vm.actions]
  );

  const handleBulkAction = useCallback(
    async (rowIds: string[], action: BulkAction) => {
      if (action.id === 'archive' && rowIds.length > 0) {
        await vm.actions.archiveDocuments(rowIds);
        setSelectedIds(new Set());
      }
    },
    [vm.actions]
  );

  const loading = vm.derived.loading || vm.status === 'loading';
  const errored = vm.status === 'error' || vm.derived.error !== null;

  return (
    <section className="pn-section pn-proj-documents" data-testid="project-documents-section" data-active-tab={vm.state.tab}>
      <SectionHead title="Documents" tag={String(vm.derived.total)} action={newMenu} />

      <ViewTabs
        options={TAB_OPTIONS.map((t) => ({ key: t.key, label: t.label }))}
        current={vm.state.tab}
        onChange={(next) => {
          vm.actions.setTab(next as DocumentsTab);
          setDocumentsView(next as DocumentsTab);
          setSelectedIds(new Set());
        }}
        ariaLabel="Project documents views"
        testIdPrefix="project-documents"
      />

      {/* Control row: search + group-by, layered on top of the existing
          Gallery/Table tabs (read-only — both issue reads, never mutations).
          The Type axis groups linked docs by their actual type, all equal,
          with an "Untyped" group; see PROJECT_DOC_GROUP_BY_OPTIONS. */}
      <div className="pn-proj-documents-controls" data-testid="project-documents-controls">
        <SearchInput
          value={vm.state.q}
          onChange={vm.actions.setSearch}
          placeholder="Search documents…"
          ariaLabel="Search project documents"
          testIdPrefix="project-documents"
        />
        <GroupByMenu
          options={PROJECT_DOC_GROUP_BY_OPTIONS}
          current={vm.state.groupBy}
          onChange={(next) =>
            vm.actions.setGroupBy(next as typeof vm.state.groupBy)
          }
          testIdPrefix="project-documents"
        />
      </div>

      {errored ? (
        <Banner kind="error" title="Couldn't load documents">
          {vm.derived.error ?? 'Refresh the page or try again.'}
        </Banner>
      ) : loading && rows.length === 0 ? (
        <div className="pn-section-loading" data-testid="project-documents-loading">
          <Skeleton width="100%" height={32} />
          <Skeleton width="100%" height={32} />
          <Skeleton width="100%" height={32} />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState data-testid="project-documents-empty">
          No documents linked to this project.
        </EmptyState>
      ) : vm.state.tab === 'gallery' ? (
        <DocumentsGallery
          rows={rows as ReadonlyArray<DocumentRow>}
          visibleChips={visibleProperties}
          groupBy={vm.state.groupBy}
          selectedIds={selectedIds}
          onToggleSelect={onToggleSelect}
          onRowOpen={onOpenDocument}
          typeLookup={typeLookup}
          ownerLookup={ownerLookup}
          systemValuesByDoc={vm.derived.systemValuesByDoc}
        />
      ) : (
        <DocumentsTable
          rows={rows as ReadonlyArray<DocumentRow>}
          systemValuesByDoc={vm.derived.systemValuesByDoc}
          typeOptions={vm.derived.typeOptions}
          memberOptions={vm.derived.memberOptions}
          typeLookup={typeLookup}
          ownerLookup={ownerLookup}
          visibleProperties={visibleProperties}
          sort={vm.state.sort}
          onSortChange={vm.actions.setSort}
          onColsChange={vm.actions.setCols}
          onRowOpen={onOpenDocument}
          onCellEdit={handleCellEdit}
          onBulkAction={handleBulkAction}
          dataTestId="project-documents"
        />
      )}

      {toast ? <Toast onDismiss={() => setToast(null)}>{toast}</Toast> : null}
    </section>
  );
}
